// =======================================================================
// SFV Tech | Apprentice Dashboard (Royal Blue / Silver Theme - Responsive v3)
// =======================================================================

import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Avatar,
  Divider,
  Breadcrumbs,
  Grow,
  CircularProgress,
  LinearProgress,
  Chip,
  useMediaQuery,
} from "@mui/material";

import {
  Dashboard,
  Receipt,
  School,
  Report,
  Notifications,
  Mail,
  Logout,
  Menu as MenuIcon,
  DoneAll,
  PendingActions,
  Refresh,
  Assignment,
  WorkspacePremium,
} from "@mui/icons-material";

import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../utils/api";
import logo from "../assets/logo.png";

export default function ApprenticeDashboard() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:900px)");

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unread, setUnread] = useState(2);
  const [notif, setNotif] = useState(1);
  const [greeting, setGreeting] = useState("");

  // Feature 2: jobs this user is really assigned to
  const [myJobs, setMyJobs] = useState([]);
  const [loadingMyJobs, setLoadingMyJobs] = useState(true);

  useEffect(() => {
    const hr = new Date().getHours();
    setGreeting(hr < 12 ? "Good Morning" : hr < 18 ? "Good Afternoon" : "Good Evening");
  }, []);

  const fetchSummary = async () => {
    try {
      const res = await api.get("/api/apprentice/summary");
      const data =
        res.data && Object.keys(res.data).length
          ? res.data
          : {
              pendingExpenses: 3,
              witnessedExpenses: 18,
              flaggedExpenses: 1,
              progress: 75,
              mentor: "Engr. A. A. Sulaiman",
              modules: [
                { name: "Electrical Safety", progress: 100 },
                { name: "Solar Installation Basics", progress: 85 },
                { name: "IoT Device Setup", progress: 60 },
              ],
            };
      setSummary(data);
    } catch {
      enqueueSnackbar("Network Error — showing demo data.", { variant: "warning" });
      setSummary({
        pendingExpenses: 3,
        witnessedExpenses: 18,
        flaggedExpenses: 1,
        progress: 75,
        mentor: "Engr. A. A. Sulaiman",
        modules: [
          { name: "Electrical Safety", progress: 100 },
          { name: "Solar Installation Basics", progress: 85 },
          { name: "IoT Device Setup", progress: 60 },
        ],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  // Feature 2: My Assigned Jobs
  useEffect(() => {
    const fetchMyJobs = async () => {
      try {
        const res = await api.get("/api/jobs/my-jobs", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setMyJobs(res.data?.jobs || []);
      } catch (e) {
        console.error("Failed to load my-jobs:", e);
      } finally {
        setLoadingMyJobs(false);
      }
    };
    fetchMyJobs();
    const interval = setInterval(fetchMyJobs, 60000);
    return () => clearInterval(interval);
  }, []);

  // Breadcrumbs
  const breadcrumbs = location.pathname
    .split("/")
    .filter(Boolean)
    .map((seg, i, arr) => (
      <Typography
        key={i}
        color={i === arr.length - 1 ? "#80d8ff" : "#fff"}
        sx={{
          cursor: "pointer",
          textTransform: "capitalize",
          fontSize: isMobile ? 12 : 14,
        }}
        onClick={() => navigate("/" + arr.slice(0, i + 1).join("/"))}
      >
        {seg}
      </Typography>
    ));

  const menuItems = [
    { text: "Dashboard", icon: <Dashboard />, path: "/apprentice/dashboard" },
    { text: "Witness Expenses", icon: <Receipt />, path: "/apprentice/expenses" },
    { text: "Training & Mentorship", icon: <School />, path: "#" },
    { text: "Reports", icon: <Report />, path: "#" },
  ];

  const cards = summary
    ? [
        {
          title: "Manage Expenses",
          desc: "Expenses awaiting your confirmation.",
          gradient: "linear-gradient(135deg, #001a33, #004c99)",
          icon: <PendingActions sx={{ fontSize: 38, color: "#80d8ff" }} />,
          link: "/apprentice/expenses",
        },
        {
          title: "Witnessed Expenses",
          desc: "Expenses you’ve reviewed and confirmed.",
          gradient: "linear-gradient(135deg, #003366, #0066cc)",
          icon: <DoneAll sx={{ fontSize: 38, color: "#80d8ff" }} />,
          link: "/apprentice/witness",
        },
        {
          title: "Auditor Page",
          desc: "Admin permission is required.",
          gradient: "linear-gradient(135deg, #002b55, #0059b3)",
          icon: <Assignment sx={{ fontSize: 38, color: "#80d8ff" }} />,
          link: "/apprentice/audit",
        },
      ]
    : [];

  return (
    <Box sx={{ minHeight: "100vh", background: "#001a33", color: "#fff" }}>

      {/* ================= TOP BAR ================= */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "linear-gradient(90deg, #002b55, #004c99)",
          py: 1.5,
          px: { xs: 1.5, sm: 3 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          boxShadow: "0 2px 12px rgba(0,0,0,0.5)",
        }}
      >
        {/* LEFT */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: "#80d8ff" }}>
            <MenuIcon />
          </IconButton>

          <img
            src={logo}
            alt="SFV"
            style={{ height: isMobile ? 26 : 32, borderRadius: 4 }}
          />

          {!isMobile && (
            <Typography fontWeight={700} variant="h6" color="#80d8ff">
              SFV Apprentice Portal
            </Typography>
          )}
        </Box>

        {/* RIGHT */}
        <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 0 : 2 }}>
          <Tooltip title="Messages">
            <IconButton sx={{ color: "#80d8ff", p: isMobile ? 0.5 : 1 }}>
              <Badge badgeContent={unread} color="error">
                <Mail />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title="Notifications">
            <IconButton sx={{ color: "#80d8ff", p: isMobile ? 0.5 : 1 }}>
              <Badge badgeContent={notif} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title="Refresh">
            <IconButton sx={{ color: "#80d8ff", p: isMobile ? 0.5 : 1 }} onClick={fetchSummary}>
              <Refresh />
            </IconButton>
          </Tooltip>

          {!isMobile && (
            <Avatar sx={{ bgcolor: "#80d8ff", color: "#001a33", fontWeight: 700 }}>
              {user?.name?.charAt(0) || "A"}
            </Avatar>
          )}
        </Box>
      </Box>

      {/* ================= SIDE DRAWER ================= */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 250, background: "#002b55", height: "100%" }}>
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="h6" fontWeight={700} color="#80d8ff">
              Apprentice Menu
            </Typography>
          </Box>

          <List>
            {menuItems.map((item) => (
              <ListItem disablePadding key={item.text}>
                <ListItemButton
                  onClick={() => {
                    navigate(item.path);
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemIcon sx={{ color: "#80d8ff" }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} sx={{ color: "#fff" }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider />

          <ListItemButton onClick={() => navigate("/")}>
            <ListItemIcon sx={{ color: "#80d8ff" }}>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Logout" sx={{ color: "#fff" }} />
          </ListItemButton>
        </Box>
      </Drawer>

      {/* ================= GREETING ================= */}
      <Box sx={{ px: { xs: 2, sm: 4 }, pt: 2 }}>
        <Breadcrumbs separator="›">{breadcrumbs}</Breadcrumbs>

        <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} mt={1}>
          {`${greeting}, ${user?.name || "Apprentice"}`} 👋
        </Typography>

        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>
      </Box>

      {/* ================= KPI CARDS ================= */}
      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <CircularProgress color="info" />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {cards.map((card, i) => (
              <Grow in timeout={400 + i * 150} key={i}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      background: card.gradient,
                      borderRadius: 3,
                      color: "#fff",
                      height: "100%",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        transition: "0.3s",
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        {card.icon}
                        <Typography variant="h6" fontWeight={700}>
                          {card.title}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                        {card.desc}
                      </Typography>

                      <Button
                        variant="contained"
                        fullWidth={isMobile}
                        sx={{
                          mt: 2,
                          backgroundColor: "#80d8ff",
                          color: "#001a33",
                          fontWeight: 700,
                        }}
                        onClick={() => navigate(card.link)}
                      >
                        Open
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grow>
            ))}
          </Grid>
        )}
      </Box>

      {/* ================= MY ASSIGNED JOBS (Feature 2) ================= */}
      <Box sx={{ px: { xs: 2, sm: 4 }, pb: 3 }}>
        <Typography variant="h6" fontWeight={700} color="#80d8ff" mb={1}>
          🧰 My Assigned Jobs
        </Typography>
        <Box
          sx={{
            background: "linear-gradient(90deg, #002b55, #003f7f)",
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            boxShadow: "0 3px 10px rgba(0,0,0,0.4)",
          }}
        >
          {loadingMyJobs ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CircularProgress size={22} color="info" />
            </Box>
          ) : myJobs.length === 0 ? (
            <Typography textAlign="center" color="#80d8ff">
              No jobs currently assigned to you.
            </Typography>
          ) : (
            myJobs.map((j) => (
              <Box
                key={j.job_id}
                onClick={() => navigate(`/jobs/${j.job_id}`)}
                sx={{
                  mb: 1,
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  cursor: "pointer",
                  background: "rgba(128,216,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  "&:hover": { background: "rgba(128,216,255,0.2)" },
                }}
              >
                <Box>
                  <Typography fontWeight={700}>{j.job_ref} — {j.title}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {j.client_name || "No client"} · {j.location || "No location"}
                  </Typography>
                  {(() => {
                    const myId = user?.user_id ?? user?.id;
                    const partners = (j.team || []).filter((t) => t.id !== myId);
                    return partners.length ? (
                      <Typography variant="caption" sx={{ display: "block", opacity: 0.65, mt: 0.25 }}>
                        With: {partners.map((p) => p.name).join(", ")}
                      </Typography>
                    ) : null;
                  })()}
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {j.open_issues > 0 && (
                    <Chip size="small" color="error" label={`${j.open_issues} open issue${j.open_issues === 1 ? "" : "s"}`} />
                  )}
                  <Chip
                    size="small"
                    label={String(j.status || "").toUpperCase()}
                    sx={{ bgcolor: j.status === "completed" ? "#2e7d32" : "#1e88e5", color: "#fff", fontWeight: 700 }}
                  />
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* ================= TRAINING & MENTORSHIP ================= */}
      {summary && (
        <Box sx={{ px: { xs: 2, sm: 4 }, pb: 3 }}>
          <Typography variant="h6" fontWeight={700} color="#80d8ff" mb={1}>
            🎓 Training & Mentorship Progress
          </Typography>

          <Box
            sx={{
              background: "linear-gradient(90deg, #002b55, #003f7f)",
              borderRadius: 3,
              p: { xs: 2, sm: 3 },
              boxShadow: "0 3px 10px rgba(0,0,0,0.4)",
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              Mentor: <span style={{ color: "#80d8ff" }}>{summary.mentor}</span>
            </Typography>

            {summary.modules.map((m, i) => (
              <Box key={i} sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  {m.name}
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={m.progress}
                  sx={{
                    height: 8,
                    borderRadius: 2,
                    backgroundColor: "#001a33",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#80d8ff",
                    },
                  }}
                />
              </Box>
            ))}

            <Chip
              icon={<WorkspacePremium />}
              label={`Overall Progress: ${summary.progress}%`}
              sx={{
                mt: 3,
                backgroundColor: "#80d8ff",
                color: "#001a33",
                fontWeight: 700,
              }}
            />
          </Box>
        </Box>
      )}

      {/* ================= FOOTER ================= */}
      <Box
        sx={{
          textAlign: "center",
          py: 2,
          mt: 3,
          background: "#002244",
          color: "#80d8ff",
          fontSize: { xs: 12, sm: 14 },
          borderTop: "1px solid #003366",
        }}
      >
        © {new Date().getFullYear()} SFV Tech. Empowering Growth through Learning.
        <br />
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          v1.0 — Apprentice Transparency Portal
        </Typography>
      </Box>
    </Box>
  );
}
